import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Compass, Package, Megaphone, Rocket, CalendarClock, ArrowUpRight } from "lucide-react";
import { InlineWidget } from "react-calendly";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, MaskedLine } from "../components/Reveal";
import { ApplicationForm } from "../components/ApplicationForm";
import { StatCounter } from "../components/StatCounter";
import { api } from "../lib/api";

const forYou = [
  "Freelance tech en régie depuis au moins 6 mois",
  "TJM entre 400€ et 900€, plafonné malgré l'expérience",
  "Tu veux un système d'acquisition qui t'appartient",
  "4 à 5h par semaine à investir dans ta bascule",
  "Tu es prêt à exécuter, pas juste à apprendre",
];

const notForYou = [
  "Tu cherches ton premier client freelance",
  "Tu veux une formation vidéo à suivre passivement",
  "Tu attends des résultats sans changer ta semaine type",
  "Tu n'es pas prêt à te rendre visible sur ton marché",
];

const steps = [
  { icon: Compass, phase: "Semaines 1-2", title: "Audit de trajectoire", text: "Cartographie complète : positionnement, actifs, marché, angles morts. On définit ton point de bascule." },
  { icon: Package, phase: "Semaines 3-6", title: "Construction de l'offre forfait", text: "On package ton expertise en offre à forte valeur perçue : périmètre, pricing, argumentaire, preuve." },
  { icon: Megaphone, phase: "Semaines 7-11", title: "Système d'acquisition", text: "Mise en place de ton moteur de visibilité : contenu, preuve sociale, pipeline de prospects qualifiés." },
  { icon: Rocket, phase: "Semaines 12-16", title: "Exécution & premiers forfaits", text: "Prospection, closing, négociation forfait. Accès direct à Simon et aux experts entre les sessions jusqu'à la signature." },
];

export default function Offre() {
  const [content, setContent] = useState(null);
  const calendlyUrl = process.env.REACT_APP_CALENDLY_URL;

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="offre-page" className="bg-[#0A0C10]">
      <Navbar />

      <section className="relative pt-40 pb-20 overflow-hidden grain">
        <div className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-primary/[0.07] blur-[120px] pointer-events-none" />
        <div className="container-site relative text-center">
          <p className="eyebrow mb-6">L'accompagnement</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            <MaskedLine delay={0.1}>Le programme qui construit</MaskedLine>
            <MaskedLine delay={0.25} className="text-primary text-glow">ta bascule régie → forfait.</MaskedLine>
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-6 text-slate-400 max-w-2xl mx-auto text-base sm:text-lg">
              16 semaines pour passer d'un TJM plafonné à un système de croissance en forfait :
              offre packagée, acquisition propre, premiers forfaits signés.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#0D1017] border-y border-[#262B3D]">
        <div className="container-site">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Reveal>
              <div data-testid="for-you-card" className="glass-card p-8 sm:p-10 h-full border-primary/30">
                <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary" /> C'est pour toi si…
                </h2>
                <ul className="space-y-4">
                  {forYou.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="not-for-you-card" className="glass-card p-8 sm:p-10 h-full opacity-90">
                <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-red-400" /> Ce n'est pas pour toi si…
                </h2>
                <ul className="space-y-4">
                  {notForYou.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-400">
                      <XCircle className="w-4 h-4 text-red-400/70 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Le déroulé</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">16 semaines, 4 phases</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`program-step-${i}`} className="glass-card p-7 h-full relative group transition-all duration-500 hover:border-primary/50 hover:-translate-y-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1">{s.phase}</span>
                  <s.icon className="w-7 h-7 text-primary mt-6 mb-4 transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="font-heading text-base font-bold mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 glass-card p-8 grid sm:grid-cols-3 gap-6 text-center" data-testid="program-format">
              <div>
                <p className="font-heading text-2xl font-extrabold text-primary">16 semaines</p>
                <p className="text-xs text-slate-500 mt-1">1 session / semaine + plan d'action</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-primary">Accès direct</p>
                <p className="text-xs text-slate-500 mt-1">à Simon et aux experts entre les sessions</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-primary">4-5h / semaine</p>
                <p className="text-xs text-slate-500 mt-1">compatible avec ta mission en cours</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {content && (
        <section className="py-16 bg-[#0D1017] border-y border-[#262B3D]">
          <div className="container-site grid grid-cols-2 lg:grid-cols-4 gap-8" data-testid="offre-stats-grid">
            {content.stats.map((s) => (
              <Reveal key={s.id} className="text-center">
                <p className="font-heading text-3xl sm:text-4xl font-extrabold text-primary">
                  <StatCounter value={s.value} suffix={s.suffix} decimals={s.decimals} testid={`offre-stat-${s.id}`} />
                </p>
                <p className="mt-2 text-xs text-slate-400 max-w-[180px] mx-auto">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="py-16 sm:py-24">
        <div className="container-site max-w-3xl text-center">
          <Reveal>
            <p className="eyebrow mb-4">L'investissement</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.15]">
              Un forfait signé rembourse <span className="text-primary">l'accompagnement.</span>
            </h2>
            <p className="mt-4 text-slate-400 text-base leading-relaxed">
              L'investissement exact dépend de ta situation et du format retenu : on en parle pendant
              l'appel découverte, sans pression et sans langue de bois. Si le programme n'est pas adapté
              à ton cas, on te le dit — et on te redirige vers les ressources gratuites.
            </p>
          </Reveal>
        </div>
      </section>

      {calendlyUrl && (
        <section className="py-16 sm:py-24 bg-[#0D1017] border-y border-[#262B3D]" data-testid="calendly-section">
          <div className="container-site max-w-4xl">
            <Reveal className="text-center mb-10">
              <p className="eyebrow mb-4 flex items-center justify-center gap-2"><CalendarClock className="w-4 h-4" /> Appel découverte</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
                Réserve 45 minutes avec Simon
              </h2>
            </Reveal>
            <div className="glass-card overflow-hidden rounded-2xl">
              <InlineWidget
                url={calendlyUrl}
                styles={{ height: "700px", minWidth: "320px", width: "100%" }}
                utm={{ utmSource: "site", utmMedium: "offre-page" }}
              />
            </div>
          </div>
        </section>
      )}

      <section id="postuler" className="py-16 sm:py-24 lg:py-32 relative grain">
        <div className="container-site max-w-3xl">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-4">Sélection</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Booke ton Check Up
            </h2>
            <p className="mt-4 text-slate-400 text-base max-w-xl mx-auto">
              L'appel découverte est réservé aux freelances dont l'activité est déjà lancée.
              Ce formulaire nous permet de préparer un échange utile — pour toi comme pour nous.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ApplicationForm testidPrefix="offre-application" />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
